// Supabase Client Configuration
// This will be configured with actual Supabase credentials later

import { createClient } from '@supabase/supabase-js';
import { Database } from '@catalog/types';

// Environment variables for Supabase
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase environment variables are not set. Using fallback configuration.');
}

// Create Supabase client with TypeScript types
export const supabase = createClient<Database>(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key'
);

// Type utilities
export type Tables<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Row'];
export type TablesInsert<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Insert'];
export type TablesUpdate<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Update'];

// Type aliases for common tables
export type Item = Tables<'items'>;
export type Category = Tables<'categories'>;
export type Location = Tables<'locations'>;
export type Merchant = Tables<'merchants'>;
export type User = Tables<'users'>;
export type ApprovedEmail = Tables<'approved_emails'>;
export type AuditLogEntry = Tables<'auth_audit_log'>;
export type ImprovementRequest = Tables<'improvement_requests'>;
export type ItemInsert = TablesInsert<'items'>;
export type ItemUpdate = TablesUpdate<'items'>;
export type ImprovementRequestInsert = TablesInsert<'improvement_requests'>;
export type ImprovementRequestUpdate = TablesUpdate<'improvement_requests'>;

// Helper function to handle Supabase errors
export const handleSupabaseError = (error: any) => {
  console.error('Supabase error:', error);
  throw new Error(error.message || 'An error occurred with the database');
};

// Check if Supabase is properly configured
export const isSupabaseConfigured = () => {
  return Boolean(supabaseUrl && supabaseAnonKey && 
    supabaseUrl !== 'https://placeholder.supabase.co' && 
    supabaseAnonKey !== 'placeholder-key'
  );
};

// Export all services
export {
  itemsService,
  categoriesService,
  locationsService,
  usersService,
  dashboardService,
  storageService,
  merchantsService,
  improvementRequestsService
} from './services';

// Security Configuration
interface AuthConfig {
  sessionTimeout: number; // in seconds
  maxLoginAttempts: number;
  lockoutDuration: number; // in seconds
  auditRetentionDays: number;
}

const DEFAULT_CONFIG: AuthConfig = {
  sessionTimeout: 3600, // 1 hour
  maxLoginAttempts: 5,
  lockoutDuration: 900, // 15 minutes
  auditRetentionDays: 90,
};

interface LoginAttempt {
  email: string;
  attempts: number;
  lastAttempt: number;
  lockedUntil?: number;
}

// In-memory store for login attempts (in production, use Redis or database)
const loginAttempts = new Map<string, LoginAttempt>();

// Simple deduplication for audit events (prevent rapid duplicates)
const recentAuditEvents = new Map<string, number>();

// Enhanced authentication service with security best practices
export const authService = {
  config: DEFAULT_CONFIG,

  // Audit logging with proper error handling and timeout
  async logAuthEvent(action: string, user?: any, metadata?: any): Promise<void> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, skipping audit log');
      return;
    }

    try {
      // Simple deduplication: prevent same action for same user within 2 seconds
      const dedupeKey = `${action}:${user?.email || 'anonymous'}`;
      const now = Date.now();
      const lastLogged = recentAuditEvents.get(dedupeKey);
      
      if (lastLogged && (now - lastLogged) < 2000) {
        console.log('🔄 Skipping duplicate audit event:', { action, email: user?.email });
        return;
      }
      
      recentAuditEvents.set(dedupeKey, now);

      const logEntry = {
        user_id: user?.id || null,
        email: user?.email || null,
        action,
        ip_address: null, // Client-side can't get real IP
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        metadata: metadata ? JSON.parse(JSON.stringify(metadata)) : null
      };

      console.log('🔍 Logging audit event:', { action, email: user?.email });

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Audit logging timeout')), 5000);
      });

      const insertPromise = supabase
        .from('auth_audit_log')
        .insert(logEntry)
        .select();

      const { data, error } = await Promise.race([insertPromise, timeoutPromise]) as any;

      if (error) {
        console.error('Failed to log auth event:', error);
        console.error('Log entry that failed:', logEntry);
      } else {
        console.log('✅ Audit event logged successfully:', data);
      }
    } catch (error) {
      console.error('Failed to log auth event (exception):', error);
      // Don't throw - let the auth flow continue
    }
  },

  // Rate limiting for login attempts
  isEmailLocked(email: string): boolean {
    const attempt = loginAttempts.get(email.toLowerCase());
    if (!attempt) return false;

    if (attempt.lockedUntil && Date.now() < attempt.lockedUntil) {
      return true;
    }

    // Clear expired lockout
    if (attempt.lockedUntil && Date.now() >= attempt.lockedUntil) {
      loginAttempts.delete(email.toLowerCase());
    }

    return false;
  },

  recordLoginAttempt(email: string, success: boolean): void {
    const key = email.toLowerCase();
    const now = Date.now();
    const attempt = loginAttempts.get(key) || { email: key, attempts: 0, lastAttempt: now };

    if (success) {
      // Reset on successful login
      loginAttempts.delete(key);
      return;
    }

    // Increment failed attempts
    attempt.attempts++;
    attempt.lastAttempt = now;

    // Lock account if too many attempts
    if (attempt.attempts >= this.config.maxLoginAttempts) {
      attempt.lockedUntil = now + (this.config.lockoutDuration * 1000);
    }

    loginAttempts.set(key, attempt);
  },

  // Enhanced sign in with rate limiting and proper error handling
  async signIn(email: string, password: string) {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot sign in');
      return { user: null, error: 'Authentication service not configured' };
    }

    const normalizedEmail = email.toLowerCase().trim();

    // Input validation
    if (!normalizedEmail || !password) {
      return { user: null, error: 'Email and password are required' };
    }

    // Check if email is locked due to too many attempts
    if (this.isEmailLocked(normalizedEmail)) {
      await this.logAuthEvent('login_failed', { email: normalizedEmail }, { reason: 'rate_limited' });
      return { 
        user: null, 
        error: 'Too many failed login attempts. Please try again later.' 
      };
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      });

      if (error) {
        this.recordLoginAttempt(normalizedEmail, false);
        await this.logAuthEvent('login_failed', { email: normalizedEmail }, { error: error.message });
        
        // Generic error message to prevent information leakage
        return { 
          user: null, 
          error: 'Invalid email or password' 
        };
      }

      this.recordLoginAttempt(normalizedEmail, true);
      await this.logAuthEvent('login_success', data.user);
      
      return { user: data.user, error: null };
    } catch (error: any) {
      this.recordLoginAttempt(normalizedEmail, false);
      await this.logAuthEvent('login_failed', { email: normalizedEmail }, { error: error.message });
      
      console.error('Sign in error:', error);
      return { user: null, error: 'An error occurred during sign-in' };
    }
  },

  // Enhanced Google OAuth with security headers
  async signInWithGoogle() {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot sign in with Google');
      return { error: 'Authentication service not configured' };
    }

    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
          scopes: 'email profile'
        }
      });

      if (error) throw error;
      
      await this.logAuthEvent('oauth_initiated', null, { provider: 'google' });
      return { data, error: null };
    } catch (error: any) {
      await this.logAuthEvent('oauth_failed', null, { provider: 'google', error: error.message });
      console.error('Google sign in error:', error);
      return { error: 'An error occurred during Google sign-in' };
    }
  },

  // Enhanced sign out with cleanup
  async signOut() {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot sign out');
      return { error: 'Authentication service not configured' };
    }

    try {
      const user = await this.getCurrentUser();
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      await this.logAuthEvent('logout', user);
      
      // Clear any cached data
      this.clearLocalCache();
      
      return { error: null };
    } catch (error: any) {
      console.error('Sign out error:', error);
      return { error: error.message };
    }
  },

  // Clear local cache/storage
  clearLocalCache(): void {
    try {
      // Clear any app-specific cached data
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('app_preferences');
        localStorage.removeItem('user_settings');
      }
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.clear();
      }
    } catch (error) {
      console.warn('Failed to clear local cache:', error);
    }
  },

  async getCurrentUser() {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot get current user');
      return null;
    }

    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      return user;
    } catch (error: any) {
      console.error('Get current user error:', error);
      return null;
    }
  },

  async getCurrentSession() {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot get session');
      return null;
    }

    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      
      // Check if session is expired
      if (session && this.isSessionExpired(session)) {
        await this.signOut();
        return null;
      }
      
      return session;
    } catch (error: any) {
      console.error('Get session error:', error);
      return null;
    }
  },

  // Session validation
  isSessionExpired(session: any): boolean {
    if (!session?.expires_at) return false;
    
    const expiresAt = new Date(session.expires_at * 1000);
    const now = new Date();
    const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
    
    return (expiresAt.getTime() - now.getTime()) < bufferTime;
  },

  // Enhanced email approval check with proper error handling
  async checkEmailApproval(email: string): Promise<{ approved: boolean; role?: string }> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot check email approval');
      return { approved: false };
    }

    const normalizedEmail = email.toLowerCase().trim();

    try {
      const { data, error } = await supabase
        .from('approved_emails')
        .select('email, role, approved, notes')
        .eq('email', normalizedEmail)
        .eq('approved', true)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        await this.logAuthEvent('email_approved', { email: normalizedEmail }, { role: data.role });
        return { approved: true, role: data.role };
      }

      await this.logAuthEvent('email_rejected', { email: normalizedEmail });
      return { approved: false };
    } catch (error: any) {
      console.error('Email approval check error:', error);
      await this.logAuthEvent('approval_check_failed', { email: normalizedEmail }, { error: error.message });
      return { approved: false };
    }
  },

  // Enhanced post-auth approval with better error handling and security
  async handlePostAuthApproval(user: any) {
    if (!user?.email) {
      throw new Error('No email found in user data');
    }

    const approvalResult = await this.checkEmailApproval(user.email);
    
    if (!approvalResult.approved) {
      // Sign out the user since they're not approved
      await this.signOut();
      await this.logAuthEvent('access_denied', user, { reason: 'email_not_approved' });
      throw new Error('Your email address is not approved for access. Please contact the administrator.');
    }

    // Check if user exists in our users table, create if not
    try {
      const { data: existingUser } = await supabase
        .from('users')
        .select('*')
        .eq('email', user.email.toLowerCase())
        .single();

      if (!existingUser) {
        // Create user record with the correct UUID from auth
        const { error } = await supabase
          .from('users')
          .insert({
            id: user.id, // Use the UUID from Supabase Auth
            email: user.email.toLowerCase(),
            name: user.user_metadata?.full_name || user.user_metadata?.name || user.email.split('@')[0],
            role: (approvalResult.role as 'admin' | 'editor' | 'viewer') || 'viewer'
          });

        if (error) {
          console.error('Error creating user record:', error);
          await this.logAuthEvent('user_creation_failed', user, { error: error.message });
        } else {
          await this.logAuthEvent('user_created', user, { role: approvalResult.role });
        }
      } else {
        // Update existing user with latest info if needed
        const updates: any = {};
        if (existingUser.name !== (user.user_metadata?.full_name || user.user_metadata?.name)) {
          updates.name = user.user_metadata?.full_name || user.user_metadata?.name || existingUser.name;
        }
        if (existingUser.role !== approvalResult.role) {
          updates.role = approvalResult.role;
        }

        if (Object.keys(updates).length > 0) {
          await supabase
            .from('users')
            .update(updates)
            .eq('id', user.id);
        }
      }
    } catch (error) {
      console.error('Error in user management:', error);
    }

    return approvalResult;
  },

  // Enhanced auth state change listener
  onAuthStateChange(callback: (event: string, session: any) => void) {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot listen to auth changes');
      return { data: { subscription: { unsubscribe: () => {} } } };
    }

    return supabase.auth.onAuthStateChange((event, session) => {
      console.log(`🔄 Supabase auth event: ${event}`, session?.user?.email || 'no user');
      
      // Call the callback immediately to not block auth flow
      callback(event, session);
      
      // Log audit events asynchronously (non-blocking)
      const significantEvents = ['SIGNED_IN', 'SIGNED_OUT', 'TOKEN_REFRESHED', 'INITIAL_SESSION'];
      
      if (significantEvents.includes(event)) {
        // Fire and forget - don't await to prevent blocking
        if (event === 'SIGNED_IN') {
          this.logAuthEvent('auth_signed_in', session?.user).catch(console.error);
        } else if (event === 'SIGNED_OUT') {
          this.logAuthEvent('auth_signed_out', session?.user).catch(console.error);
        } else if (event === 'TOKEN_REFRESHED') {
          this.logAuthEvent('token_refreshed', session?.user).catch(console.error);
        } else if (event === 'INITIAL_SESSION' && session?.user) {
          this.logAuthEvent('auth_initial_session', session?.user).catch(console.error);
        }
      }
    });
  },

  // Session refresh with better error handling
  async refreshSession() {
    if (!isSupabaseConfigured()) {
      return { session: null, error: 'Authentication service not configured' };
    }

    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) throw error;

      await this.logAuthEvent('session_refreshed', data.user);
      return { session: data.session, error: null };
    } catch (error: any) {
      console.error('Session refresh error:', error);
      await this.logAuthEvent('session_refresh_failed', null, { error: error.message });
      return { session: null, error: error.message };
    }
  },

  // Security utilities for admin use
  async getSecurityStats() {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot get security stats');
      return null;
    }

    try {
      const { data: recentLogs, error } = await supabase
        .from('auth_audit_log')
        .select('action, created_at, user_id')
        .order('created_at', { ascending: false })
        .limit(1000);

      if (error) throw error;

      const now = new Date();
      const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      
      const recent = recentLogs?.filter(log => new Date(log.created_at) > last24Hours) || [];
      
      return {
        totalLogins: recent.filter(log => log.action === 'login_success').length,
        failedLogins: recent.filter(log => log.action === 'login_failed').length,
        suspiciousActivity: recent.filter(log => 
          ['access_denied', 'login_failed', 'approval_check_failed'].includes(log.action)
        ).length,
        uniqueUsers: new Set(recent.map(log => log.user_id)).size,
      };
    } catch (error) {
      console.error('Error fetching security stats:', error);
      return null;
    }
  },

  // Clean up old audit logs (should be run periodically)
  async cleanupAuditLogs() {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot cleanup audit logs');
      return;
    }

    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - this.config.auditRetentionDays);

      const { error } = await supabase
        .from('auth_audit_log')
        .delete()
        .lt('created_at', cutoffDate.toISOString());

      if (error) throw error;
      
      console.log(`Cleaned up audit logs older than ${cutoffDate.toISOString()}`);
    } catch (error) {
      console.error('Error cleaning up audit logs:', error);
    }
  }
};

// Export auth service
export const auth = authService; 